import styled from "@emotion/styled";
import {
  Background,
  Border,
  Chip,
  CircularProgress,
  ITextTheme,
  TextBold,
  TextColor,
  TextLetterSpacing,
  TextLineHeight,
  TextSize,
  TextThemeContext,
  Typography,
} from "@ibeckinc/ui-elements";
import { List } from "immutable";
import Head from "next/head";
import { useSession } from "next-auth/react";
import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { ArticleList } from "../../components/ArticleList";
import { BookmarkButton } from "../../components/BookmarkButton";
import {
  appendBookmarkArticle,
  deductBookmarkArticle,
} from "../../components/BookmarkButton/query";
import { Head as CustomHead } from "../../components/Head";
import { ContentLayout } from "../../components/Layout";
import { LikeButton } from "../../components/LikeButton";
import {
  appendLikeArticle,
  deductLikeArticle,
} from "../../components/LikeButton/query";
import Logo from "../../components/Logo";
import RegistrationLeadingModal from "../../components/Modal/RegistrationLeadingModal";
import { XPostButton } from "../../components/XPostButton";
import { useMobile } from "../../hooks/useMobile";
import { useWindowSize } from "../../hooks/useWindowSize";
import { pagesPath } from "../../lib/$path";
import { ArticleColor } from "../../lib/constants/Article";
import { DATE_FORMAT } from "../../lib/constants/Date";
import { device, formatDate } from "../../utils";
import { useGetArticle } from "./query";
import { SignupPromotion } from "./SignupPromotion";

const { Paragraphs, Text, Title } = Typography;

type Props = {
  bookmarkIds: string[];
  id: string;
  keyword: string;
  isAuthenticated: boolean;
  likeIds: string[];
  fullURL: string;
};

const Template: FunctionComponent<Props> = ({
  bookmarkIds,
  id,
  keyword,
  isAuthenticated,
  likeIds,
  fullURL,
}) => {
  const { isMobile } = useMobile();
  const { width: windowWidth } = useWindowSize();
  const textTheme = useContext(TextThemeContext);
  const { data: session, status } = useSession();

  const { data: article } = useGetArticle({ key: keyword });

  const [_bookmarkIds, setBookmarkIds] = useState<List<string>>(
    List(bookmarkIds),
  );
  const [_likeIds, setLikeIds] = useState<List<string>>(List(likeIds));

  const handleAppendBookmark = useCallback(async () => {
    if (session) {
      await appendBookmarkArticle({ id, session });
      setBookmarkIds(_bookmarkIds.push(id));
    }
  }, [id, _bookmarkIds, session]);

  const handleDeductBookmark = useCallback(async () => {
    if (session) {
      await deductBookmarkArticle({ id, session });
      setBookmarkIds(
        _bookmarkIds.remove(_bookmarkIds.findIndex((_id) => _id === id)),
      );
    }
  }, [id, _bookmarkIds, session]);

  const handleAppendLike = useCallback(async () => {
    if (session) {
      await appendLikeArticle({ id, session });
      setLikeIds(_likeIds.push(id));
    }
  }, [id, _likeIds, session]);

  const handleDeductLike = useCallback(async () => {
    if (session) {
      await deductLikeArticle({ id, session });
      setLikeIds(_likeIds.remove(_likeIds.findIndex((_id) => _id === id)));
    }
  }, [id, _likeIds, session]);

  const contents = useMemo(() => article?.contents.split(","), [article]);
  const updatedAt = useMemo(
    () =>
      article?.updatedAt
        ? formatDate(article.updatedAt, DATE_FORMAT.JP_SIMPLE_DATE_FORMAT)
        : "",
    [article],
  );

  if (!article) return <CircularProgress overlay />;

  return (
    <>
      <CustomHead
        title={article.title}
        description={article.description}
        image={article.headerImageUrl}
        href={pagesPath.articles._id(keyword).$url()}
      />
      <Head>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            image: [article.thumbnailUrl, article.headerImageUrl],
            url: fullURL,
            datePublished: article.publishedAt,
            dateModified: article.updatedAt,
            author: [
              {
                "@type": "Organization",
                name: "iBECK Inc.",
                url: "https://www.ibeck.co.jp/",
              },
            ],
          })}
        </script>
      </Head>
      <PageCenterPlacer>
        <ActionContainer>
          <LikeButton
            defaultChecked={_likeIds.includes(id)}
            onAppend={handleAppendLike}
            onDeduct={handleDeductLike}
          />
          <BookmarkButton
            defaultChecked={_bookmarkIds.includes(id)}
            onAppend={handleAppendBookmark}
            onDeduct={handleDeductBookmark}
          />
          <XPostButton customText={article.title} url={fullURL} />
        </ActionContainer>
      </PageCenterPlacer>
      <ContentLayout
        breadcrumbs={[
          { item: "記事一覧", path: pagesPath.articles.$url().pathname },
          {
            item: article.title,
            path: pagesPath.articles._id(keyword).$url().pathname,
          },
        ]}
        breadcrumbsBottom
      >
        <Root>
          <React.Suspense fallback={<CircularProgress />}>
            {article && (
              <>
                <Header
                  backgroundImage={article.headerImageUrl}
                  windowWidth={windowWidth}
                  headerOnlyImageUsed={article.headerOnlyImageUsed}
                >
                  {!article.headerOnlyImageUsed ? (
                    <HeaderContent color={ArticleColor[article.color]}>
                      <ArticleTitle
                        alignment={isMobile() ? "start" : "center"}
                        color="white"
                        level={isMobile() ? 4 : 1}
                        multiline
                      >
                        {article.title}
                      </ArticleTitle>
                      <Tags>
                        {article.tags.map(({ id, name }) => (
                          <Chip key={id} label={name} variant="transparent" />
                        ))}
                      </Tags>
                      <Caption>
                        <Text bold="light" color="white">
                          {`著者: ${article.author}`}
                        </Text>
                        <UpdatedAt color="white">
                          更新日:{" "}
                          <time dateTime={article.updatedAt.slice(0, 10)}>
                            {updatedAt}
                          </time>
                        </UpdatedAt>
                      </Caption>
                    </HeaderContent>
                  ) : (
                    <HeaderContentContainer>
                      <BlindH1>{article.title}</BlindH1>
                      <SimpleTags>
                        {article.tags.map(({ id, name }) => (
                          <Chip key={id} label={name} variant="default" />
                        ))}
                      </SimpleTags>
                      <Text bold="light" color="blueBlack">
                        {`著者: ${article.author}`}
                      </Text>
                      <UpdatedAt color="blueBlack">
                        更新日:{" "}
                        <time dateTime={article.updatedAt.slice(0, 10)}>
                          {updatedAt}
                        </time>
                      </UpdatedAt>
                    </HeaderContentContainer>
                  )}
                </Header>
                <Contents>
                  <Paragraphs color="light">目次</Paragraphs>
                  <ContentsOrderedList>
                    {contents?.map((content, idx) => (
                      <ContentsOrderedListItem key={`article-contents-${idx}`}>
                        <ContentAnchor href={`#${idx}-${content}`}>
                          <Text color="light" variant="body">
                            {`${idx + 1}. ${content}`}
                          </Text>
                        </ContentAnchor>
                      </ContentsOrderedListItem>
                    ))}
                  </ContentsOrderedList>
                </Contents>
                {article.isPrivate && !isAuthenticated ? (
                  <SignupPromotion />
                ) : (
                  <>
                    <Body>
                      <BodyHTML
                        h2Theme={textTheme.h2}
                        h3Theme={textTheme.h3}
                        dangerouslySetInnerHTML={{ __html: article.body }}
                      />
                    </Body>
                    <AuthorProfileContainer>
                      <AuthorProfile>
                        {/* 著者が作成されるようになったらLogoから変更 */}
                        <Logo height={32} />
                        <Text bold="bold">{article.author}</Text>
                      </AuthorProfile>
                      <Text color="blueBlack" multiline>
                        {article.authorProfile}
                      </Text>
                    </AuthorProfileContainer>
                  </>
                )}
                <ArticleList
                  align="left"
                  excludeIds={[id]}
                  limit={3}
                  tagId={article?.tags[0]?.id}
                  title="関連記事"
                  hideEmpty
                />
              </>
            )}
          </React.Suspense>
          {status === "unauthenticated" && <RegistrationLeadingModal />}
        </Root>
      </ContentLayout>
    </>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
  width: 100%;
  position: relative;

  @media ${device.mobile} {
    padding: 0;
  }
`;

const PageCenterPlacer = styled.div`
  width: 30px;
  z-index: 5;

  @media ${device.mobile} {
    background: ${Background.Base};
    position: fixed;
    right: 72px;
    bottom: 16px;
  }

  @media ${device.pc} {
    height: 0;
    position: sticky;
    left: 50%;
    top: 0;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 16px 0;
  position: absolute;
  left: 400px;
  top: 200px;

  @media ${device.mobile} {
    flex-direction: row;
    gap: 8px;
    left: 0px;
    top: -64px;
  }
`;

const Header = styled.div<{
  backgroundImage: string;
  windowWidth: number;
  headerOnlyImageUsed: boolean;
}>`
  background: url(${({ backgroundImage }) => backgroundImage}) center top / auto
    100% no-repeat;
  display: flex;
  align-items: ${({ headerOnlyImageUsed }) =>
    headerOnlyImageUsed ? "flex-end" : "center"};
  justify-content: center;
  margin: 0;
  height: 570px;
  width: ${({ windowWidth }) => windowWidth - 20}px;
  z-index: 10;

  @media ${device.pc} {
    min-width: 1000px;
  }

  @media ${device.mobile} {
    margin: 0;
    width: 100vw;
    height: 276px;
  }
`;

const HeaderContent = styled.div<{
  color: string;
}>`
  background: ${({ color }) => color};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 98px;
  max-width: 1000px;
  height: 400px;
  width: 100%;
  position: relative;

  @media ${device.mobile} {
    align-items: flex-start;
    gap: 16px;
    margin: 16px;
    padding: 24px 16px;
    height: 244px;
  }
`;

const HeaderContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 auto -36px;
  max-width: 1000px;
  width: 100%;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin: 0 auto -96px;
  }
`;

const BlindH1 = styled.h1`
  overflow: hidden;
  position: absolute;
  margin: 0;
  padding: 0;
  border: 0;
  width: 0;
  height: 0;
  font-size: 0;
  line-height: 0;
  visibility: hidden;
  background: none;
`;

const ArticleTitle = styled(Title)`
  margin-top: 92px;
  overflow-wrap: break-all;
  width: 85%;

  @media ${device.mobile} {
    margin: 0;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0 64px;

  @media ${device.mobile} {
    align-content: flex-start;
    margin: 0;
  }
`;

const SimpleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  align-items: center;

  @media ${device.mobile} {
    align-content: flex-start;
    margin: 0;
  }
`;

const Caption = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media ${device.mobile} {
    justify-content: flex-start;
    margin: 0;
  }
`;

const UpdatedAt = styled.p<{ color: "white" | "blueBlack" }>`
  color: ${({ color }) =>
    color === "white" ? TextColor.white : TextColor.blueBlack};
  font-family: "Noto Sans JP";
  font-size: ${TextSize.medium};
  font-weight: ${TextBold.light};
  margin: 0;
`;

const Contents = styled.div`
  background: ${Background.Light};
  box-sizing: border-box;
  margin: 0 auto;
  padding: 24px 48px;
  width: 540px;

  @media ${device.mobile} {
    margin: 48px auto 0;
    padding: 16px;
    width: 100%;
  }
`;

const ContentsOrderedList = styled.ol`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
`;

const ContentsOrderedListItem = styled.li`
  &:not(:first-of-type) {
    padding-top: 8px;
  }

  &:not(:last-of-type) {
    padding-bottom: 8px;
    border-bottom: 1px solid ${Border.Base};
  }
`;

const ContentAnchor = styled.a`
  text-decoration: none;
`;

const Body = styled.div`
  margin: 50px auto 80px;
  width: 100%;
  max-width: 720px;

  @media ${device.mobile} {
    margin: 24px 0 48px;
    max-width: unset;
    width: 100%;
  }
`;

const BodyHTML = styled.div<{ h2Theme: ITextTheme; h3Theme: ITextTheme }>`
  h2 {
    color: ${({ h2Theme }) => TextColor[h2Theme.color ?? "base"]};
    font-size: ${({ h2Theme }) => TextSize[h2Theme.size ?? "regular"]};
    font-weight: ${({ h2Theme }) => TextBold[h2Theme.bold ?? "bold"]};
    letter-spacing: ${({ h2Theme }) =>
      TextLetterSpacing[h2Theme.letterSpacing ?? "default"]};
    line-height: ${({ h2Theme }) =>
      TextLineHeight[h2Theme.lineHeight ?? "regular"]};
  }

  h2.article-heading-second {
    box-sizing: border-box;
    margin: 0 0 16px;
  }

  h2.article-heading-second::before {
    content: "";
    display: inline-block;
    background: ${Background.Brand};
    height: 32px;
    width: 5px;
    margin: 0px 22px -6px 0;

    @media ${device.mobile} {
      margin: 0px 16px -4px 0;
      height: 24px;
    }
  }

  h3 {
    color: ${({ h3Theme }) => TextColor[h3Theme.color ?? "base"]};
    font-size: ${({ h3Theme }) => TextSize[h3Theme.size ?? "regular"]};
    font-weight: ${({ h3Theme }) => TextBold[h3Theme.bold ?? "bold"]};
    letter-spacing: ${({ h3Theme }) =>
      TextLetterSpacing[h3Theme.letterSpacing ?? "default"]};
    line-height: ${({ h3Theme }) =>
      TextLineHeight[h3Theme.lineHeight ?? "regular"]};
  }

  h3.article-heading-third {
    margin: 0 0 16px 0;
  }

  @media ${device.mobile} {
    h2 {
      font-size: ${TextSize.regular};
    }
  }
`;

const AuthorProfileContainer = styled.div`
  border: 1px solid ${Border.Base};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  padding: 24px;
  max-width: 720px;
  width: 100%;
`;

const AuthorProfile = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${Border.Base};
  display: flex;
  align-items: center;
  gap: 16px;
  max-height: 80px;
  padding: 0 0 16px;
  width: 100%;
`;

export default Template;
